import { useState, useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import { getCalendarTimeline, getProperties } from '@/lib/api';
import { TenantDetailModal } from '@/components/TenantDetailModal';
import { LeadDetailModal } from '@/components/calendar/LeadDetailModal';
import { TimelineHeader } from '@/components/calendar/TimelineHeader';
import { PropertyGroup } from '@/components/calendar/PropertyGroup';
import { TodayMarker } from '@/components/calendar/TodayMarker';
import { dateToX, DAY_WIDTH, HEADER_HEIGHT, PROPERTY_HEADER_HEIGHT, ROW_HEIGHT, LEFT_COL_WIDTH, COLORS, setResponsiveCalendarDims, subscribeCalendarDims } from '@/components/calendar/calendarConstants';
import { UnitYearSummaryButton, revenueForRange, occupiedDays } from '@/components/calendar/MonthRevenueOverlay';
import { formatRent } from '@/components/calendar/calendarConstants';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { differenceInDays, differenceInCalendarDays, parseISO, eachMonthOfInterval, startOfYear } from 'date-fns';

export default function CalendarPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [tenantDetailId, setTenantDetailId] = useState(null);
  const [leadDetailId, setLeadDetailId] = useState(null);
  const scrollRef = useRef(null);
  const leftColRef = useRef(null);

  useEffect(() => { getProperties().then(setProperties).catch(() => {}); }, []);

  // Responsive calendar dims: toggle DAY_WIDTH and LEFT_COL_WIDTH on mobile,
  // and force a re-render whenever they change so every sub-component
  // (TimelineHeader, PropertyGroup, BookingBar, …) picks up the new values.
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setResponsiveCalendarDims({ isMobile: mq.matches });
    apply();
    const listener = () => apply();
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else mq.addListener(listener);
    const unsub = subscribeCalendarDims(forceUpdate);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', listener);
      else mq.removeListener(listener);
      unsub();
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProperty !== 'all') params.property_id = selectedProperty;
      const result = await getCalendarTimeline(params);
      setData(result);
    } catch { toast.error('Failed to load timeline data'); }
    finally { setLoading(false); }
  }, [selectedProperty]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { rangeStart, rangeEnd, months, totalWidth, today, totalHeight } = useMemo(() => {
    if (!data) return { rangeStart: new Date(), rangeEnd: new Date(), months: [], totalWidth: 0, today: new Date(), totalHeight: 0 };
    const rs = parseISO(data.range_start);
    const re = parseISO(data.range_end);
    const t = parseISO(data.today);
    const ms = eachMonthOfInterval({ start: rs, end: re });
    const tw = differenceInDays(re, rs) * DAY_WIDTH;
    let th = HEADER_HEIGHT;
    for (const prop of data.properties) {
      th += PROPERTY_HEADER_HEIGHT;
      th += prop.units.length * ROW_HEIGHT;
    }
    return { rangeStart: rs, rangeEnd: re, months: ms, totalWidth: tw, today: t, totalHeight: th };
  }, [data, DAY_WIDTH]);

  // Portfolio year-to-date rollup — measured over completed full months
  // of the current year (Jan → end of previous month). Current month is
  // NOT counted.
  //
  //  • YTD INCOME  = sum of ALL listed-unit income across those months.
  //                  NO available_since filter — matches rent-roll totals.
  //  • AVG / MONTH = YTD income ÷ number of completed months this year.
  //  • PER UNIT / MONTH = simple mean of each unit's OWN eligible-months
  //    average. A unit's eligible months = full months of the current
  //    year that ended on/after its available_since AND whose 1st day
  //    is on/after its available_since (i.e. unit was available for
  //    the whole month). Units with 0 eligible months are excluded
  //    from this mean.
  //  • OCCUPANCY   = day-level. Sum(occupied days) ÷ Sum(available days
  //    per unit, from max(Jan 1, available_since) → end of prev month).
  const portfolioYtd = useMemo(() => {
    if (!data || !data.properties?.length) return null;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const yStart = startOfYear(now);
    const endOfPrevMonth = new Date(currentYear, currentMonth - 1, 0);
    const completedMonthCount = currentMonth - 1;

    let totalIncome = 0;
    let occSum = 0;
    let availDaysSum = 0;
    let unitCount = 0;
    let listedCount = 0;
    const perUnitAvgs = [];

    for (const prop of data.properties) {
      for (const unit of prop.units) {
        unitCount += 1;
        if (unit.is_listed === false) continue;
        listedCount += 1;

        const activationStr = unit?.active_since || unit?.availability_start_date || unit?.open_date || unit?.created_at || unit?.listed_at;
        const activationDate = activationStr ? parseISO(activationStr) : null;
        const monthlyIncome = unit.monthly_income || [];

        let unitRev = 0;
        let unitEligibleIncome = 0;
        let unitEligibleMonths = 0;
        for (const entry of monthlyIncome) {
          if (entry.year !== currentYear) continue;
          if (entry.month < 1 || entry.month >= currentMonth) continue;
          unitRev += entry.income || 0;
          const mStart = new Date(entry.year, entry.month - 1, 1);
          if (!activationDate || activationDate <= mStart) {
            unitEligibleIncome += entry.income || 0;
            unitEligibleMonths += 1;
          }
        }
        totalIncome += unitRev;
        if (unitEligibleMonths > 0) {
          perUnitAvgs.push(unitEligibleIncome / unitEligibleMonths);
        }

        const occStart = activationDate && activationDate > yStart ? activationDate : yStart;
        if (occStart <= endOfPrevMonth) {
          const d = differenceInCalendarDays(endOfPrevMonth, occStart) + 1;
          availDaysSum += d;
          occSum += occupiedDays(occStart, endOfPrevMonth, unit.bookings || []);
        }
      }
    }

    if (!listedCount || completedMonthCount === 0) return null;
    const rev = Math.round(totalIncome);
    const avgPerMonth = Math.round(rev / completedMonthCount);
    const avgPerUnitPerMonth = perUnitAvgs.length
      ? Math.round(perUnitAvgs.reduce((a, b) => a + b, 0) / perUnitAvgs.length)
      : null;
    const occPct = availDaysSum > 0 ? Math.round((occSum / availDaysSum) * 100) : 0;
    return {
      rev, avgPerMonth, avgPerUnitPerMonth, occPct,
      unitCount, listedCount, year: currentYear
    };
  }, [data]);

  useEffect(() => {
    if (data && scrollRef.current) {
      const todayX = dateToX(today, rangeStart);
      scrollRef.current.scrollLeft = Math.max(0, todayX - 200);
    }
  }, [data, today, rangeStart]);

  const scrollBy = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: direction * 30 * DAY_WIDTH * 3, behavior: 'smooth' });
  };

  // Sync vertical scroll between timeline and left column
  const handleTimelineScroll = () => {
    if (scrollRef.current && leftColRef.current) {
      leftColRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  };

  return (
    <div className="space-y-5" data-testid="calendar-timeline-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Occupancy Timeline</h1>
          <p className="text-sm text-muted-foreground mt-1">Scroll horizontally to view occupancy across months</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedProperty} onValueChange={setSelectedProperty} data-testid="calendar-property-selector">
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="calendar-property-selector-trigger">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => scrollBy(-1)} data-testid="calendar-scroll-left-button">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" data-testid="calendar-scroll-today-button"
              onClick={() => { if (scrollRef.current) { scrollRef.current.scrollTo({ left: Math.max(0, dateToX(today, rangeStart) - 200), behavior: 'smooth' }); } }}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => scrollBy(1)} data-testid="calendar-scroll-right-button">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {portfolioYtd && (
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border/70 bg-card px-4 py-2.5 shadow-sm"
          data-testid="calendar-portfolio-ytd"
        >
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {portfolioYtd.year} portfolio
            </span>
            <span className="text-[11px] text-muted-foreground">
              {portfolioYtd.listedCount}
              {portfolioYtd.listedCount === portfolioYtd.unitCount ? '' : ` / ${portfolioYtd.unitCount}`}{' '}
              active {portfolioYtd.listedCount === 1 ? 'unit' : 'units'}
            </span>
          </div>
          <div className="h-8 w-px bg-border/70 hidden sm:block" />
          <div className="flex flex-col" data-testid="calendar-portfolio-ytd-revenue">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">YTD income</span>
            <span className="text-base font-semibold tabular-nums">{formatRent(portfolioYtd.rev)}</span>
          </div>
          <div className="h-8 w-px bg-border/70 hidden sm:block" />
          <div className="flex flex-col" data-testid="calendar-portfolio-ytd-avg">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg / month</span>
            <span className="text-base font-semibold tabular-nums">{formatRent(portfolioYtd.avgPerMonth)}</span>
          </div>
          <div className="h-8 w-px bg-border/70 hidden sm:block" />
          <div className="flex flex-col" data-testid="calendar-portfolio-ytd-avg-per-unit">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Per unit / month</span>
            <span className="text-base font-semibold tabular-nums">{portfolioYtd.avgPerUnitPerMonth != null ? formatRent(portfolioYtd.avgPerUnitPerMonth) : '—'}</span>
          </div>
          <div className="h-8 w-px bg-border/70 hidden sm:block" />
          <div className="flex flex-col" data-testid="calendar-portfolio-ytd-occupancy">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Occupancy</span>
            <span className="text-base font-semibold tabular-nums">{portfolioYtd.occPct}%</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-8 rounded" style={{ backgroundColor: COLORS.booking.bg }} />
          <span className="text-muted-foreground">Long-term Tenant</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-8 rounded" style={{ backgroundColor: COLORS.airbnb.bg }} />
          <span className="text-muted-foreground">Airbnb / VRBO</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-8 rounded" style={{ backgroundColor: COLORS.vacant }} />
          <span className="text-muted-foreground">Vacant</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-8 rounded" style={{
            border: `1.5px dashed ${COLORS.lead.bg}`,
            background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${COLORS.lead.stripe} 2px, ${COLORS.lead.stripe} 4px)`,
          }} />
          <span className="text-muted-foreground">Lead Interest</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-0.5 rounded" style={{ backgroundColor: COLORS.todayLine }} />
          <span className="text-muted-foreground">Today</span>
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!loading && data && data.properties.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CalendarIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-semibold">No properties to display</h3>
            <p className="text-sm text-muted-foreground mt-1">Add properties and units to see the timeline</p>
          </CardContent>
        </Card>
      )}

      {!loading && data && data.properties.length > 0 && (
        <Card className="border border-border/70" data-testid="calendar-timeline-card">
          <div className="flex" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            {/* Fixed left column */}
            <div
              ref={leftColRef}
              className="shrink-0 overflow-hidden border-r bg-card"
              style={{ width: LEFT_COL_WIDTH }}
              data-testid="calendar-left-column"
            >
              {/* Header cell */}
              <div className="flex items-end pb-2 px-2 border-b bg-card" style={{ height: HEADER_HEIGHT }}>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">Unit</span>
              </div>
              {/* Property groups - left labels only */}
              {data.properties.map((property) => (
                <div key={property.property_id}>
                  <div className="flex items-center px-2 bg-muted border-b gap-1" style={{ height: PROPERTY_HEADER_HEIGHT }}>
                    <span className="text-xs font-semibold tracking-tight truncate">{property.property_name}</span>
                    <Badge variant="outline" className="hidden sm:inline-flex text-[10px] px-1 py-0 shrink-0">
                      {property.units.length}
                    </Badge>
                  </div>
                  {property.units.map((unit) => (
                    <div key={unit.unit_id} className="border-b bg-card" style={{ height: ROW_HEIGHT }} data-testid="calendar-left-unit-label">
                      <UnitYearSummaryButton unit={unit} testId={`unit-year-summary-${unit.unit_id}`} className="px-2">
                        <div className="flex items-center gap-1.5 h-full min-w-0">
                          <Badge variant="secondary" className="text-[11px] font-medium px-1.5 py-0.5 shrink-0">{unit.unit_number}</Badge>
                          <span className="hidden md:inline text-[11px] text-muted-foreground truncate">{unit.unit_size}</span>
                        </div>
                      </UnitYearSummaryButton>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {/* Scrollable timeline */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-auto relative"
              onScroll={handleTimelineScroll}
              data-testid="calendar-timeline-scroll"
            >
              <TimelineHeader months={months} rangeStart={rangeStart} totalWidth={totalWidth} leftColumnHidden />
              {data.properties.map((property) => (
                <PropertyGroup key={property.property_id} property={property} rangeStart={rangeStart} rangeEnd={rangeEnd}
                  months={months} totalWidth={totalWidth} onTenantClick={(tid) => setTenantDetailId(tid)} onLeadClick={(lid) => setLeadDetailId(lid)} leftColumnHidden />
              ))}
              <TodayMarker today={today} rangeStart={rangeStart} totalHeight={totalHeight} />
            </div>
          </div>
        </Card>
      )}

      <TenantDetailModal tenantId={tenantDetailId} open={!!tenantDetailId} onClose={() => setTenantDetailId(null)} />
      <LeadDetailModal leadId={leadDetailId} open={!!leadDetailId} onClose={() => setLeadDetailId(null)} />
    </div>
  );
}
